import React, { FC } from 'react';
import styled from '@emotion/styled';
import { useTheme } from '@emotion/react';
import { Palette, Box, Flex, Text } from '@updater/ui-uds';
import { ProgressGate, getDaysText, useDaysLeft } from './utils';
import { ArrowRight } from '@phosphor-icons/react';
import { CompletedSegment, ValidatingSegment } from './progress-bar-styles';

const Card = styled(Box)`
  background-color: ${({ theme }) => theme.colors.white};
  border-radius: 1rem;
  padding: 0.75rem 1.25rem;
  min-height: 6.125rem;
  box-shadow: ${({ theme }) => theme.shadows.shadow10};
`;

const BarContainer = styled(Box)`
  width: 100%;
  height: 0.5rem;
  background-color: ${Palette.mint100}; /* no theme.colors equivalent for mint10 */
  border-radius: 0.25rem;
  margin: 0.5rem 0;
`;

export interface ProgressBarCardProps {
  /** Every count comes from `getProgressGate`; this card derives none itself. */
  progress: ProgressGate;
  currentMoveDate: string | null | undefined;
  onStartTask?: () => void;
}

export const ProgressBarCard: FC<ProgressBarCardProps> = ({
  progress,
  currentMoveDate,
  onStartTask,
}) => {
  const theme = useTheme();
  const daysLeft = useDaysLeft(currentMoveDate);

  const {
    totalTasks,
    completedTasks,
    validatingTasks,
    remaining: remainingTasks,
    pct,
    completedPct,
    validatingPct,
  } = progress;

  let title: string;
  if (pct === 100) title = "You're all set";
  else if (remainingTasks === 1) title = 'Just one more!';
  else if (pct === 50) title = "You're halfway there!";
  else if (pct > 50) title = 'Over halfway there!';
  else if (pct === 0) title = 'Get started';
  else title = 'Moving progress';

  if (totalTasks === 0) {
    return null;
  }

  return (
    <Card data-testid="progress-bar-card">
      <Flex alignItems="center" justifyContent="space-between">
        {pct === 0 ? (
          <Flex
            alignItems="center"
            justifyContent="space-between"
            width="100%"
            onClick={() => onStartTask?.()}
            style={{ cursor: 'pointer' }}
          >
            <Text variant="lBold" style={{ color: theme.colors.cta }}>
              {title}
            </Text>
            <ArrowRight size={24} color={theme.colors.cta} />
          </Flex>
        ) : (
          <>
            <Text variant="lBold">{title}</Text>
            <Text variant="lBold">{pct}%</Text>
          </>
        )}
      </Flex>

      <BarContainer>
        <Flex width="100%" height="100%">
          {completedPct > 0 && (
            <CompletedSegment
              widthPct={completedPct}
              hasValidating={validatingPct > 0}
              data-tooltip="Completed tasks"
            />
          )}
          {validatingPct > 0 && (
            <ValidatingSegment
              widthPct={validatingPct}
              hasCompleted={completedPct > 0}
              data-tooltip="Tasks in review"
            />
          )}
        </Flex>
      </BarContainer>

      <Flex justifyContent="space-between" flexWrap="wrap" gap="0.25rem">
        {pct === 0 ? (
          <Text variant="s">{getDaysText(daysLeft, currentMoveDate)}</Text>
        ) : (
          <>
            <Text variant="s">
              {completedTasks > 0 && `● ${completedTasks} done`}{' '}
              {validatingTasks > 0 && `● ${validatingTasks} in review`}{' '}
              {remainingTasks > 0 &&
                `● ${remainingTasks} priority task${
                  remainingTasks === 1 ? '' : 's'
                } remaining`}
            </Text>
            <Text variant="s">{getDaysText(daysLeft, currentMoveDate)}</Text>
          </>
        )}
      </Flex>
    </Card>
  );
};
